<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose: `Ce cookie est utilisé pour distinguer les humains des robots. Cela est utile pour le site web afin de générer des rapports valides sur l'utilisation du site.`,
            expiry: '180 jours',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: `Stocke l'état du consentement aux cookies de l'utilisateur pour le domaine actuel`,
            expiry: '1 an',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose: `Utilisé par le système de gestion de contenu (CMS) du site web pour déterminer comment les menus d'onglets du site doivent être affichés.`,
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose: `Utilisé par le système de gestion de contenu (CMS) du site web pour déterminer comment les menus d'onglets du site doivent être affichés.`,
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose: `Ce cookie est utilisé pour distinguer les humains des robots. Cela est utile pour le site web afin de générer des rapports valides sur l'utilisation du site.`,
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Ce cookie est utilisé pour distinguer les humains des robots.',
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Ce cookie est utilisé pour distinguer les humains des robots.',
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Ce cookie est utilisé pour distinguer les humains des robots.',
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose: 'Détermine la langue préférée du visiteur. Permet au site web de définir la langue préférée lors du retour du visiteur.',
            expiry: '1 an',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose: `Utilisé pour envoyer des données à Google Analytics concernant l'appareil et le comportement de l'utilisateur. Suit l'utilisateur à travers les appareils et les canaux de marketing.`,
            expiry: '2 ans',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose: `Utilisé pour envoyer des données à Google Analytics concernant l'appareil et le comportement de l'utilisateur. Suit l'utilisateur à travers les appareils et les canaux de marketing.`,
            expiry: '2 ans',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Détermine le thème préféré du visiteur.',
            expiry: 'Persistant',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Politique de Cookies">
        <p>
            Nous utilisons des cookies pour personnaliser le contenu et les publicités, pour fournir des fonctionnalités de médias sociaux et pour
            analyser notre trafic. Nous partageons également des informations sur votre utilisation de notre site avec nos partenaires d'analyse web,
            de publicité et de médias sociaux, qui peuvent les combiner avec d'autres informations.
        </p>
        <p>
            Les cookies sont de petits fichiers texte qui peuvent être utilisés par les sites web pour rendre l'expérience utilisateur plus efficace.
        </p>
        <p>
            La loi stipule que nous pouvons stocker des cookies sur votre appareil s'ils sont strictement nécessaires au fonctionnement de ce site.
            Pour tous les autres types de cookies, nous avons besoin de votre consentement.
        </p>
        <p>Ce site utilise différents types de cookies. Certains cookies sont placés par des services tiers qui apparaissent sur nos pages.</p>
        <p>Vous pouvez modifier ou retirer votre consentement à tout moment depuis la Déclaration relative aux cookies sur notre site web.</p>
        <p>
            Découvrez-en plus sur qui nous sommes, comment vous pouvez nous contacter et comment nous traitons les données personnelles dans notre
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Politique de confidentialité</NuxtLinkLocale>.
        </p>
        <p>Indiquez votre ID de consentement et la date à laquelle vous nous avez contactés concernant votre consentement.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Nécessaires (8)</h3>
            <p>
                Les cookies nécessaires contribuent à rendre un site web utilisable en activant des fonctions de base comme la navigation sur les
                pages et l'accès aux zones sécurisées du site web. Le site web ne peut pas fonctionner correctement sans ces cookies.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Préférences (1)</h3>
            <p>
                Les cookies de préférences permettent à un site web de mémoriser des informations qui modifient le comportement ou l'apparence du site
                web, comme votre langue préférée ou la région où vous vous trouvez.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Les cookies de marketing sont utilisés pour suivre les visiteurs sur les sites web. L'intention est d'afficher des publicités
                pertinentes et attrayantes pour l'utilisateur individuel et donc plus précieuses pour les éditeurs et les annonceurs tiers.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Non classifiés (1)</h3>
            <p>Les cookies non classifiés sont des cookies en cours de classification, ainsi que les fournisseurs de cookies individuels.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
